export class OrgAdminApiError extends Error {
    code;
    status;
    constructor(code, message, status, options) {
        super(message, options);
        this.code = code;
        this.status = status;
        this.name = "OrgAdminApiError";
        this.details = options?.details;
    }
    details;
}
/**
 * Lexserver Zod-validated routes ship raw Zod issue arrays as the
 * `error.message` string. Surface the first issue as `field: message`
 * so users see something actionable instead of a JSON dump.
 *
 * TODO(post-MVP): once lexserver ships a structured `error.details`
 * field for validation errors (see ai-docs/org-admin-runtime-config-plan.md
 * §8.2 "Out of scope"), this client-side stringified-array parsing can
 * retire. Only handles JSON-encoded *array* shapes today; single-issue
 * objects fall through unchanged.
 *
 * Exported for unit testing — pure helper, no side effects.
 */
export function formatErrorMessage(raw) {
    if (typeof raw !== "string" || raw.length === 0 || raw[0] !== "[")
        return raw;
    try {
        const issues = JSON.parse(raw);
        if (!Array.isArray(issues) || issues.length === 0)
            return raw;
        const first = issues[0];
        if (!first || typeof first !== "object")
            return raw;
        const path = Array.isArray(first.path) ? first.path.filter((p) => typeof p === "string" || typeof p === "number").join(".") : "";
        const msg = typeof first.message === "string" ? first.message : "Invalid value";
        const more = issues.length > 1 ? ` (+${issues.length - 1} more)` : "";
        return path ? `${path}: ${msg}${more}` : `${msg}${more}`;
    }
    catch {
        return raw;
    }
}
/**
 * Typed fetch wrapper for the lexserver Management REST API (org-scoped endpoints).
 *
 * URL construction: `${baseUrl}/org/${orgId}/...`
 * - LexPortal: baseUrl="/lexserver/api" → /lexserver/api/org/{orgId}/users → proxy → lexserver
 * - LexIDE:    baseUrl="/api"           → /api/org/{orgId}/users           → proxy → lexserver
 */
export class OrgAdminClient {
    baseUrl;
    orgId;
    getToken;
    constructor(baseUrl, orgId, getToken) {
        this.baseUrl = baseUrl;
        this.orgId = orgId;
        this.getToken = getToken;
    }
    async request(method, path, body, attempt = 0) {
        const token = await this.getToken();
        const headers = {};
        if (body)
            headers["Content-Type"] = "application/json";
        if (token)
            headers["Authorization"] = `Bearer ${token}`;
        let res;
        try {
            res = await fetch(`${this.baseUrl}/org/${this.orgId}${path}`, {
                method,
                headers,
                body: body ? JSON.stringify(body) : undefined,
            });
        }
        catch (err) {
            // Network failure (DNS, connection refused, browser offline). The
            // raw "TypeError: Failed to fetch" is a poor UX signal — translate
            // to a friendly message keyed off a stable error code consumers can
            // branch on. Preserve the original via Error.cause so devtools +
            // Sentry can still surface the underlying failure for debugging.
            throw new OrgAdminApiError("NETWORK_ERROR", "Cannot reach the server. Check your connection and try again.", 0, { cause: err });
        }
        // 429 auto-retry — the lexserver limiter (100 req/min/sub) rejects the
        // request BEFORE any handler runs, so retrying is always safe (any
        // method). Honor Retry-After (the limiter sends the window remainder,
        // ≤60s), cap the wait, add jitter against a multi-tab stampede, and
        // bound the attempts. Without this the page surfaced a terminal
        // "Rate limit exceeded" error that needed a manual reload (operator
        // report 2026-07-13) — now the request just resolves late instead.
        if (res.status === 429 && attempt < 2) {
            const retryAfterSec = Number(res.headers.get("Retry-After") ?? "1");
            const boundedSec = Number.isFinite(retryAfterSec)
                ? Math.min(Math.max(retryAfterSec, 0), 30)
                : 1;
            await new Promise((r) => setTimeout(r, boundedSec * 1000 + Math.random() * 400));
            return this.request(method, path, body, attempt + 1);
        }
        // Parse the body. Lexserver always returns JSON for both success and
        // error responses, but the proxy layer (Vite dev proxy, nginx) may
        // emit empty / HTML bodies on backend down or 5xx — `res.json()`
        // would throw the unhelpful "Unexpected end of JSON input" string in
        // that case.
        const text = await res.text();
        // Widened from `{code, message}` so server-supplied extra fields
        // (e.g. the LLM_VALIDATION_FAILED 422's typed `validation` payload)
        // survive into OrgAdminApiError.details. Routes that only return
        // {code, message} keep working unchanged.
        let json;
        try {
            json = text ? JSON.parse(text) : {};
        }
        catch {
            throw new OrgAdminApiError(res.ok ? "INVALID_RESPONSE" : "SERVER_ERROR", res.ok
                ? "Received an unexpected response from the server."
                : `Server returned ${res.status} ${res.statusText || ""}`.trim(), res.status);
        }
        if (!json.ok) {
            // 5xx with no structured body usually means the upstream service is
            // down — the proxy returns "Internal Server Error" / "Bad Gateway"
            // statusText which is jargon to end-users. Translate to a friendly
            // message; keep specific error.message when the server actually
            // returned one.
            const fallback = res.status >= 500
                ? "Server is unreachable right now. Please try again in a moment."
                : res.statusText || "Request failed";
            const err = json.error ?? { code: "UNKNOWN", message: fallback };
            // Pull every server-supplied field beyond {code, message} into
            // `details`. Empty object becomes undefined so consumers can
            // simply `if (err.details)` rather than checking emptiness.
            const { code: _code, message: _message, ...rest } = err;
            void _code;
            void _message;
            const details = Object.keys(rest).length > 0 ? rest : undefined;
            throw new OrgAdminApiError(err.code, formatErrorMessage(err.message), res.status, { details });
        }
        return json;
    }
    buildQuery(params) {
        if (!params)
            return "";
        const qs = new URLSearchParams();
        for (const [k, v] of Object.entries(params)) {
            if (v !== undefined && v !== "")
                qs.set(k, String(v));
        }
        const q = qs.toString();
        return q ? `?${q}` : "";
    }
    // ── Users ───────────────────────────────────────────────────
    async listUsers(params) {
        return this.request("GET", `/users${this.buildQuery(params)}`);
    }
    async getUser(userId) {
        return this.request("GET", `/users/${userId}`);
    }
    async createUser(input) {
        return this.request("POST", "/users", input);
    }
    /**
     * Patch a user. Send ONLY the fields that changed.
     *
     * ⚠ `email` is accepted **only while the invitation is still `sent`** —
     * the server revokes the pending Clerk invitation, writes the new address
     * and re-issues to it, all in one operation (doing that as three client
     * calls would strand a user on a revoked invitation with the old email if
     * the tab closed mid-sequence). Once a user has ACCEPTED, their email is
     * their Clerk sign-in credential and the server rejects the change with
     * `EMAIL_IMMUTABLE_AFTER_ACCEPT`; a collision inside the org rejects with
     * `DUPLICATE_EMAIL`. Callers should branch on those codes rather than
     * rendering the raw message.
     *
     * ⚠ The response is the authority on what was actually applied. A server
     * that predates the email support silently DROPS the field (Zod strip
     * mode) and still answers 200 — so a caller that assumes success from the
     * status code alone will tell the operator an address changed when it did
     * not. Compare the returned row against what you sent.
     *
     * On a successful email change two byproduct fields ride FLAT on the user
     * payload (pinned with lexserver, thread 20260804T194800Z):
     *
     *   - `invitationUrl` — the re-issued link. Optional-never-empty: absent
     *     means the re-issue produced no URL, so never render a placeholder.
     *   - `emailSent` — whether DELIVERY succeeded. Minting an invitation and
     *     delivering it fail separately, and `false` here is the silent case:
     *     the row looks correct, the invitation exists, and nobody ever
     *     receives it. When it is `false`, `invitationUrl` is the recovery
     *     path and belongs on screen.
     *
     * ⚠ Both are byproducts of THIS call, not properties of the user — no
     * other user-returning route emits them. Read them, render them, drop
     * them; never persist them into a cached user object.
     */
    async updateUser(userId, patch) {
        return this.request("PATCH", `/users/${userId}`, patch);
    }
    async deleteUser(userId) {
        return this.request("DELETE", `/users/${userId}`);
    }
    /**
     * Revoke the prior invitation (best-effort, idempotent) and issue a
     * fresh one. Returns the updated user row plus the new invitation URL
     * so the operator can share it directly (Clerk's hosted email is also
     * sent, but `+clerk_test` addresses get no real delivery — the URL is
     * the manual-share path). Rejects 409 when the user already accepted
     * or is a legacy bound-Clerk-identity user.
     */
    async resendInvitation(userId) {
        return this.request("POST", `/users/${userId}/invitation/resend`);
    }
    /**
     * Invalidate a pending invitation (status `sent` → `revoked`). The
     * Clerk-hosted URL stops working. Idempotent server-side. Rejects 409
     * for any non-`sent` invitation state.
     */
    async revokeInvitation(userId) {
        return this.request("POST", `/users/${userId}/invitation/revoke`);
    }
    /**
     * Accept the invitation server-side on behalf of the user — creates a
     * Clerk identity with a known password and binds `clerkUserId` +
     * `invitationStatus="accepted"` to the Mongo row. PLATFORM_ADMIN only:
     * the lexserver endpoint 403s ORG_ADMIN callers because accept-on-behalf
     * bypasses user consent.
     *
     * Two intended use cases:
     *   1) `+clerk_test` addresses that never receive a real Clerk email —
     *      the no-friction unblock for test-account admins. (Production
     *      pipeline auto-accepts these at provision time; this endpoint is
     *      the retroactive path for orgs provisioned before the pipeline
     *      step shipped.)
     *   2) A real user who lost their email link — operator unblocks by
     *      accepting, then instructs the user to use Clerk's "Forgot
     *      password" reset flow to set their own password.
     *
     * Idempotent: an already-accepted user returns the existing row (200)
     * without calling Clerk.
     */
    async acceptInvitation(userId) {
        return this.request("POST", `/users/${userId}/invitation/accept`);
    }
    // ── Settings ────────────────────────────────────────────────
    async getSettings() {
        return this.request("GET", "/settings");
    }
    async updateSettings(patch) {
        return this.request("PATCH", "/settings", patch);
    }
    // ── Runtime Config ──────────────────────────────────────────
    async getRuntimeConfig() {
        return this.request("GET", "/runtime-config");
    }
    async updateRuntimeConfig(patch) {
        return this.request("PATCH", "/runtime-config", patch);
    }
    /**
     * Resolve a chatProfile name (bare or composite) via the platform's
     * llmProfiles catalog WITHOUT saving. Returns the resolver's decision
     * (provider, model, thinkingDefault, maxOutputTokens, temperature) OR
     * a typed `error` discriminator on bad shape / unknown base / unresolvable.
     *
     * Used by the RuntimeConfigPage ProfilePicker to surface the resolved
     * model inline as the operator picks — org-admin has NO read access
     * to the catalog itself, so the preview is the only way to make the
     * selection meaningful at the picker boundary.
     */
    async resolveChatProfile(name) {
        return this.request("GET", `/runtime-config/resolve-chat-profile?name=${encodeURIComponent(name)}`);
    }
    /**
     * GET the org's LLM status — the per-alias effective ladder rung
     * (`effectiveTiers`) + providerHealth. Drives the §8.9 AliasMatrix's
     * live "resolves ✓ / health" column. `effectiveTiers` is absent when the
     * org has no alias table (absence invariant).
     */
    async getLlmStatus() {
        return this.request("GET", "/runtime-config/llm-status");
    }
    /**
     * S5 — the org-plane search status: which credential actually serves each
     * provider, and which providers the server knows about.
     *
     * ⚠ ORG PLANE ONLY. The platform-admin twin (`/api/search-providers/:orgId/
     * status`, `requirePlatformAdmin()`) answers the same question for staff and
     * must never enter this client — LexIDE ships this bundle to ORG admins, and
     * a platform-scoped capability in it would 403 for most of its audience.
     */
    async getSearchStatus() {
        return this.request("GET", "/runtime-config/search-status");
    }
    // ── LLM Provider OAuth (Phase 5 Slice 5.2) ──────────────────
    //
    // Org-scoped surface for the editable `RuntimeConfigPage` OAuth
    // subsection. Reads consume the shared non-secret status view
    // (no token/apiKey material is ever returned).
    // Writes flow through the lexserver's two-step manual-code OAuth
    // pipeline:
    //   1) `initLlmOauth` returns the upstream provider URL + flow id.
    //   2) Operator opens the URL in a new tab, authorises, pastes the
    //      returned code back into the UI.
    //   3) `completeLlmOauth` exchanges (flowId, code) → tokens; lexserver
    //      writes them (encrypted) into `runtimeConfig.llm.authProfilesJson`.
    // `disconnectLlmOauth` removes the provider's profile from the store.
    //
    // SAME-PROCESS constraint: init + complete MUST hit the same lexserver
    // replica (module-global flow registry). Single-replica or session
    // affinity in prod — see plan §Risks "distributed flow registry".
    async getLlmOauthStatus() {
        return this.request("GET", "/llm-oauth");
    }
    async initLlmOauth(provider) {
        return this.request("POST", `/llm-oauth/${provider}/init`);
    }
    async completeLlmOauth(provider, body) {
        return this.request("POST", `/llm-oauth/${provider}/complete`, body);
    }
    async disconnectLlmOauth(provider) {
        return this.request("DELETE", `/llm-oauth/${provider}`);
    }
    // ── Audit Logs ──────────────────────────────────────────────
    async listAuditLogs(params) {
        return this.request("GET", `/audit-logs${this.buildQuery(params)}`);
    }
}
//# sourceMappingURL=org-admin-client.js.map